import { formatNumberAsPrice } from '@vfde-brix/ws10/core';
import {
    IOptionPickerTextItem,
    OPTION_PICKER_TYPE_TEXT,
    OptionPicker,
} from '@vfde-brix/ws10/option-picker';
import { startAppListening } from '../../../app/listener';
import {
    RootState,
    useAppDispatch,
} from '../../../app/store';
import { mountOptionPicker } from '../../../components/OptionPicker';
import {
    selectCapacityOptions,
    selectAvailableCapacitiesForColor,
} from '../selectors';
import {
    changeCapacity,
    selectCurrentCapacity,
    selectCustomerConfig,
} from '../slice';

/**
 * Mount the color picker
 */
export const mountCapacityOptionPicker = (containerId: string) => {
    const dispatch = useAppDispatch();

    const onChange = (event: Event) => {
        const target = event.target as HTMLInputElement;
        dispatch(changeCapacity(parseInt(target.value, 10)));
    };

    const items: IOptionPickerTextItem[] = [];

    const optionPicker = mountOptionPicker(
        containerId,
        'option-picker-capacity',
        OPTION_PICKER_TYPE_TEXT,
        items,
        onChange,
    );

    /* istanbul ignore if */
    if (!optionPicker) {
        return null;
    }

    listenForUpdates(optionPicker);

    return optionPicker;
};

const listenForUpdates = (optionPicker: OptionPicker) => {
    startAppListening({
        predicate: (_action, currentState, previousState) =>
            selectCapacityOptions(currentState) !== selectCapacityOptions(previousState)
            || selectAvailableCapacitiesForColor(currentState) !== selectAvailableCapacitiesForColor(previousState)
            || selectCurrentCapacity(currentState) !== selectCurrentCapacity(previousState)
            || selectCustomerConfig(currentState).isHardwareOnly !== selectCustomerConfig(previousState).isHardwareOnly
        ,
        effect: (_action, listenerApi) => {
            const state = listenerApi.getState();

            updateCapacities(state, optionPicker);
        },
    });
};

const updateCapacities = (state: RootState, optionPicker: OptionPicker) => {
    const items = convertCapacitiesFromApiToCapacityOptionPickerItems(state);

    optionPicker.update({ items });
};

const convertCapacitiesFromApiToCapacityOptionPickerItems = (state: RootState): IOptionPickerTextItem[] | undefined => {
    const capacityOptions = selectCapacityOptions(state);
    const capacitiesForColor = selectAvailableCapacitiesForColor(state);
    const currentCapacity = selectCurrentCapacity(state);
    const customerConfig = selectCustomerConfig(state);
    const { isHardwareOnly } = customerConfig;
    const items: IOptionPickerTextItem[] = [];

    /* istanbul ignore if */
    if (!capacityOptions || !capacitiesForColor) {
        // availableCapacities & capacitiesForColor could be null
        return items;
    }

    const availableSizes = capacitiesForColor!.map(capacity => capacity.sortValue);

    for (const capacity of capacityOptions!) {
        const optChecked = currentCapacity === capacity.sortValue;
        const optDisabled = !availableSizes.includes(capacity.sortValue);
        const price = isHardwareOnly ? capacity.priceHardwareOnly : capacity.priceHardwareOneTimeWithTariff;

        items.push({
            stdValue: `${capacity.sortValue}`,
            stdPrimaryLabel: capacity.displayLabel,
            stdSecondaryLabel: formatNumberAsPrice(price.gross),
            optChecked,
            optDisabled,
        });
    }

    return items;
};
